import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './config';
import { formatLocalDate, formatLocalDateTime, formatLocalTime, parseServerDateTime } from './timezone';

const WORKFLOW_COLORS = [
  '#6c63ff', '#22c55e', '#f59e0b', '#3b82f6', '#ec4899',
  '#14b8a6', '#f97316', '#8b5cf6', '#06b6d4', '#84cc16',
  '#a855f7', '#ef4444',
];

const STATUS_COLORS = {
  completed: '#22c55e',
  failed: '#ef4444',
  running: '#f59e0b',
  cancelled: '#9ca3af',
};

// Flag a day cell when this many or more workflows are scheduled at the same hour
const COLLISION_THRESHOLD = 3;

function getWorkflowColor(id, index) {
  return WORKFLOW_COLORS[index % WORKFLOW_COLORS.length];
}

function fmtTime(value) {
  return formatLocalTime(value, { hour: '2-digit', minute: '2-digit' });
}

function fmtDateTime(value) {
  return formatLocalDateTime(value, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day + (day === 0 ? -6 : 1)); // Monday start
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function isSameDay(d1, d2) {
  return d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();
}

// Returns 42 day cells covering the month (Mon-start grid)
function getMonthGrid(monthStart) {
  const first = new Date(monthStart.getFullYear(), monthStart.getMonth(), 1);
  const gridStart = startOfWeek(first);
  return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
}

const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const statusBadgeStyle = (status) => {
  const map = {
    completed: { bg: 'rgba(34,197,94,0.15)', color: '#22c55e' },
    failed: { bg: 'rgba(239,68,68,0.15)', color: '#ef4444' },
    running: { bg: 'rgba(245,158,11,0.15)', color: '#f59e0b' },
    paused: { bg: 'rgba(156,163,175,0.15)', color: '#9ca3af' },
    cancelled: { bg: 'rgba(156,163,175,0.15)', color: '#9ca3af' },
  };
  const c = map[status] || map.paused;
  return {
    display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
    fontSize: '11px', fontWeight: 600, background: c.bg, color: c.color,
    textTransform: 'capitalize',
  };
};

export default function SchedulerCalendar({ onBack, token, workspaceId, onOpenWorkflow }) {
  const [entries, setEntries] = useState([]);
  const [pastRuns, setPastRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('month'); // 'month' | 'week' | 'timeline'
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [monthStart, setMonthStart] = useState(() => {
    const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d;
  });
  const [togglingId, setTogglingId] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null); // run detail side panel

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchPastRuns = useCallback(async (entriesList) => {
    if (!entriesList.length) return;
    setLoadingRuns(true);
    try {
      const results = await Promise.all(
        entriesList.map((entry, idx) =>
          axios.get(`${API_BASE_URL}/workflows/${entry.id}/runs`, {
            headers: authHeaders,
            params: { limit: 100, skip: 0 },
          })
            .then(r => ({ entry, idx, runs: r.data.runs || [] }))
            .catch(() => ({ entry, idx, runs: [] }))
        )
      );
      const allRuns = [];
      results.forEach(({ entry, idx, runs }) => {
        const color = getWorkflowColor(entry.id, idx);
        runs.forEach(run => {
          if (run.start_time) allRuns.push({ ...run, color, entry });
        });
      });
      setPastRuns(allRuns);
    } finally {
      setLoadingRuns(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { next_n: 20 };
      if (workspaceId) params.workspace_id = workspaceId;
      const res = await axios.get(`${API_BASE_URL}/workflows/schedule-calendar`, {
        headers: authHeaders, params,
      });
      setEntries(res.data);
      return res.data;
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load schedule data');
      return [];
    } finally {
      setLoading(false);
    }
  }, [workspaceId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = useCallback(async () => {
    const data = await fetchCalendar();
    await fetchPastRuns(data);
  }, [fetchCalendar, fetchPastRuns]);

  useEffect(() => { handleRefresh(); }, [handleRefresh]);

  const togglePause = async (entry) => {
    setTogglingId(entry.id);
    try {
      await axios.patch(
        `${API_BASE_URL}/workflows/${entry.id}/schedule`,
        { is_paused: !entry.is_paused },
        { headers: authHeaders }
      );
      await handleRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to update schedule');
    } finally {
      setTogglingId(null);
    }
  };

  // ── Build event lists ──────────────────────────────────────────────────────

  // Upcoming projected events (ghost-styled on calendar)
  const upcomingEvents = [];
  entries.forEach((entry, idx) => {
    const color = getWorkflowColor(entry.id, idx);
    (entry.next_run_times || []).forEach(t => {
      const d = parseServerDateTime(t);
      if (d) upcomingEvents.push({ entry, time: d, color, type: 'upcoming' });
    });
  });

  // Past run events (status-colored, clickable)
  const pastEvents = pastRuns.map(run => {
    const d = parseServerDateTime(run.start_time);
    return d ? { run, entry: run.entry, time: d, color: run.color, type: 'past' } : null;
  }).filter(Boolean);

  // Per-day event buckets for a given set of days
  function buildEventsByDay(days) {
    const byDay = {};
    days.forEach(d => { byDay[d.toDateString()] = { past: [], upcoming: [] }; });
    pastEvents.forEach(ev => {
      const key = ev.time.toDateString();
      if (byDay[key]) byDay[key].past.push(ev);
    });
    upcomingEvents.forEach(ev => {
      const key = ev.time.toDateString();
      if (byDay[key]) byDay[key].upcoming.push(ev);
    });
    Object.values(byDay).forEach(({ past, upcoming }) => {
      past.sort((a, b) => a.time - b.time);
      upcoming.sort((a, b) => a.time - b.time);
    });
    return byDay;
  }

  // Collision detection: days where ≥ COLLISION_THRESHOLD workflows fire in the same hour
  function computeOverloaded(days) {
    const overloaded = {}; // dateString -> [{hour, count, workflows[]}]
    days.forEach(day => {
      const key = day.toDateString();
      const hourBuckets = {};
      upcomingEvents
        .filter(ev => isSameDay(ev.time, day) && !ev.entry.is_paused)
        .forEach(ev => {
          const h = ev.time.getHours();
          if (!hourBuckets[h]) hourBuckets[h] = [];
          hourBuckets[h].push(ev);
        });
      const cols = Object.entries(hourBuckets)
        .filter(([, evs]) => evs.length >= COLLISION_THRESHOLD)
        .map(([h, evs]) => ({
          hour: parseInt(h, 10),
          count: evs.length,
          workflows: [...new Set(evs.map(e => e.entry.name))],
        }));
      if (cols.length) overloaded[key] = cols;
    });
    return overloaded;
  }

  // Week view data
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const weekEventsByDay = buildEventsByDay(weekDays);
  const weekOverloaded = computeOverloaded(weekDays);

  // Month view data
  const monthGrid = getMonthGrid(monthStart);
  const monthEventsByDay = buildEventsByDay(monthGrid);
  const monthOverloaded = computeOverloaded(monthGrid);

  // Upcoming timeline (sorted)
  const allUpcoming = [...upcomingEvents].sort((a, b) => a.time - b.time);

  // ── Shared style helpers ───────────────────────────────────────────────────

  const tabBtn = (active) => ({
    padding: '6px 16px', borderRadius: '6px',
    border: `1px solid ${active ? 'var(--primary-color)' : 'var(--border-color)'}`,
    background: active ? 'var(--primary-color)' : 'transparent',
    color: active ? '#fff' : 'var(--text-secondary)',
    cursor: 'pointer', fontSize: '13px', fontWeight: 500,
  });

  const navBtn = (extra = {}) => ({
    padding: '5px 12px', borderRadius: '6px', border: '1px solid var(--border-color)',
    background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer',
    fontSize: '13px', ...extra,
  });

  // ── Event chip renderer ────────────────────────────────────────────────────

  const renderChip = (ev, i, compact = false) => {
    if (ev.type === 'past') {
      const sc = STATUS_COLORS[ev.run.status] || '#9ca3af';
      return (
        <div
          key={`p-${ev.run.id}-${i}`}
          onClick={() => setSelectedEvent(ev)}
          title={`${ev.entry.name}\n${ev.run.status}\n${fmtDateTime(ev.time)}`}
          style={{
            background: `${sc}22`, border: `1px solid ${sc}55`,
            borderLeft: `3px solid ${sc}`, borderRadius: '4px',
            padding: compact ? '2px 4px' : '3px 6px',
            fontSize: compact ? '10px' : '11px', cursor: 'pointer', overflow: 'hidden',
          }}
        >
          <div style={{ fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {compact ? ev.entry.name.slice(0, 14) : ev.entry.name}
          </div>
          {!compact && (
            <div style={{ color: sc, fontSize: '10px' }}>
              {fmtTime(ev.time)} · {ev.run.status}
            </div>
          )}
        </div>
      );
    }
    // Upcoming ghost event
    const col = ev.entry.is_paused ? '#9ca3af' : ev.color;
    return (
      <div
        key={`u-${ev.entry.id}-${i}`}
        title={`${ev.entry.name} (scheduled)\n${fmtDateTime(ev.time)}`}
        style={{
          background: `${col}11`, border: `1px dashed ${col}55`,
          borderLeft: `3px solid ${col}88`, borderRadius: '4px',
          padding: compact ? '2px 4px' : '3px 6px',
          fontSize: compact ? '10px' : '11px', overflow: 'hidden',
          opacity: ev.entry.is_paused ? 0.5 : 0.85,
        }}
      >
        <div style={{ fontWeight: 500, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {compact ? ev.entry.name.slice(0, 14) : ev.entry.name}
        </div>
        {!compact && (
          <div style={{ color: 'var(--text-tertiary)', fontSize: '10px' }}>{fmtTime(ev.time)}</div>
        )}
      </div>
    );
  };

  // ── Calendar day cell ──────────────────────────────────────────────────────

  const renderDayCell = (day, eventsByDay, overloaded, compact = false) => {
    const key = day.toDateString();
    const { past = [], upcoming = [] } = eventsByDay[key] || {};
    const allEvs = [...past, ...upcoming];
    const isToday = isSameDay(day, new Date());
    const isCurrentMonth = !compact || day.getMonth() === monthStart.getMonth();
    const collision = overloaded[key];
    const maxShow = compact ? 3 : 6;
    const overflow = allEvs.length > maxShow;
    const dowIndex = (day.getDay() + 6) % 7; // 0=Mon … 6=Sun

    return (
      <div
        key={key}
        style={{
          background: 'var(--bg-secondary)',
          borderRadius: compact ? '6px' : '8px',
          border: collision
            ? '2px solid #ef4444'
            : isToday
            ? '2px solid var(--primary-color)'
            : '1px solid var(--border-color)',
          minHeight: compact ? '88px' : '160px',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          opacity: isCurrentMonth ? 1 : 0.4,
          position: 'relative',
        }}
      >
        {/* Collision badge */}
        {collision && (
          <div
            title={collision.map(c =>
              `${c.count} workflows at ${String(c.hour).padStart(2, '0')}:00 — consider staggering`
            ).join('\n')}
            style={{
              position: 'absolute', top: 4, right: 4, zIndex: 2,
              background: '#ef4444', color: '#fff', borderRadius: '50%',
              width: 14, height: 14, fontSize: 9, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'help',
            }}
          >!</div>
        )}

        {/* Day header */}
        <div style={{
          padding: compact ? '4px 7px' : '8px 10px',
          borderBottom: '1px solid var(--border-color)',
          background: isToday ? 'rgba(108,99,255,0.15)' : 'var(--bg-tertiary)',
          display: 'flex', alignItems: 'center', gap: 4,
        }}>
          {!compact && (
            <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginRight: 4 }}>
              {DOW_LABELS[dowIndex]}
            </span>
          )}
          <span style={{ fontSize: compact ? '13px' : '18px', fontWeight: 700, color: isToday ? 'var(--primary-color)' : 'var(--text-primary)' }}>
            {day.getDate()}
          </span>
          {compact && collision && (
            <span style={{ marginLeft: 'auto', fontSize: '10px', color: '#ef4444', fontWeight: 700 }}>⚡</span>
          )}
        </div>

        {/* Events */}
        <div style={{ flex: 1, padding: 4, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}>
          {allEvs.length === 0 && (
            <span style={{ color: 'var(--text-tertiary)', fontSize: '10px', padding: '2px 2px' }}>—</span>
          )}
          {allEvs.slice(0, maxShow).map((ev, i) => renderChip(ev, i, compact))}
          {overflow && (
            <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', padding: '1px 4px' }}>
              +{allEvs.length - maxShow} more
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Full render ────────────────────────────────────────────────────────────

  return (
    <div style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary)', color: 'var(--text-primary)', overflow: 'hidden' }}>

      {/* Header */}
      <div style={{ padding: '14px 24px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0, flexWrap: 'wrap' }}>
        <button onClick={onBack} style={navBtn()}>← Back</button>
        <span style={{ fontSize: '17px', fontWeight: 700 }}>Scheduler Calendar</span>

        {/* View tabs */}
        <div style={{ display: 'flex', gap: '6px', marginLeft: '12px' }}>
          <button style={tabBtn(viewMode === 'month')} onClick={() => setViewMode('month')}>Month</button>
          <button style={tabBtn(viewMode === 'week')} onClick={() => setViewMode('week')}>Week</button>
          <button style={tabBtn(viewMode === 'timeline')} onClick={() => setViewMode('timeline')}>Upcoming</button>
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', fontSize: '11px', color: 'var(--text-secondary)', marginLeft: '8px', flexWrap: 'wrap' }}>
          {[
            { color: '#22c55e', label: 'completed', solid: true },
            { color: '#ef4444', label: 'failed', solid: true },
            { color: '#f59e0b', label: 'running', solid: true },
            { color: '#6c63ff', label: 'scheduled', solid: false },
          ].map(({ color, label, solid }) => (
            <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 9, height: 9, borderRadius: 2, background: solid ? color : 'transparent', border: solid ? 'none' : `1.5px dashed ${color}`, display: 'inline-block', flexShrink: 0 }} />
              {label}
            </span>
          ))}
          <span style={{ display: 'flex', alignItems: 'center', gap: 3, color: '#ef4444' }}>
            <span>⚡</span> collision
          </span>
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {(loading || loadingRuns) && <span style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>Loading…</span>}
          <button
            onClick={handleRefresh}
            disabled={loading || loadingRuns}
            style={{ ...navBtn(), background: loading || loadingRuns ? 'var(--bg-tertiary)' : 'var(--primary-color)', color: '#fff', border: 'none', opacity: loading || loadingRuns ? 0.5 : 1 }}
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: '10px 24px', background: 'rgba(239,68,68,0.1)', color: '#ef4444', borderBottom: '1px solid var(--border-color)', fontSize: '13px' }}>
          {error}
        </div>
      )}

      {/* Body + optional run-detail side panel */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

          {/* ── MONTH VIEW ── */}
          {viewMode === 'month' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <button style={navBtn()} onClick={() => setMonthStart(m => { const d = new Date(m); d.setMonth(d.getMonth() - 1); return d; })}>‹ Prev</button>
                <span style={{ fontWeight: 600, fontSize: '15px', minWidth: 160, textAlign: 'center' }}>
                  {formatLocalDate(monthStart, { month: 'long', year: 'numeric' })}
                </span>
                <button style={navBtn()} onClick={() => setMonthStart(m => { const d = new Date(m); d.setMonth(d.getMonth() + 1); return d; })}>Next ›</button>
                <button style={navBtn({ marginLeft: 4, fontSize: '12px' })} onClick={() => { const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); setMonthStart(d); }}>Today</button>
              </div>

              {/* DOW header row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
                {DOW_LABELS.map(d => (
                  <div key={d} style={{ textAlign: 'center', fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', padding: '3px 0' }}>{d}</div>
                ))}
              </div>

              {/* 6-row × 7-col grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
                {monthGrid.map(day => renderDayCell(day, monthEventsByDay, monthOverloaded, true))}
              </div>

              <WorkflowControls entries={entries} loading={loading} togglingId={togglingId} onToggle={togglePause} onOpen={onOpenWorkflow} />
            </div>
          )}

          {/* ── WEEK VIEW ── */}
          {viewMode === 'week' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <button style={navBtn()} onClick={() => setWeekStart(w => addDays(w, -7))}>‹ Prev</button>
                <span style={{ fontWeight: 600, fontSize: '15px', minWidth: 240, textAlign: 'center' }}>
                  {formatLocalDate(weekStart, { month: 'long', day: 'numeric' })}
                  {' – '}
                  {formatLocalDate(addDays(weekStart, 6), { month: 'long', day: 'numeric', year: 'numeric' })}
                </span>
                <button style={navBtn()} onClick={() => setWeekStart(w => addDays(w, 7))}>Next ›</button>
                <button style={navBtn({ marginLeft: 4, fontSize: '12px' })} onClick={() => setWeekStart(startOfWeek(new Date()))}>Today</button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
                {weekDays.map(day => renderDayCell(day, weekEventsByDay, weekOverloaded, false))}
              </div>

              <WorkflowControls entries={entries} loading={loading} togglingId={togglingId} onToggle={togglePause} onOpen={onOpenWorkflow} />
            </div>
          )}

          {/* ── TIMELINE VIEW ── */}
          {viewMode === 'timeline' && (
            <div>
              <h3 style={{ margin: '0 0 14px', fontSize: '14px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                Upcoming Runs — {allUpcoming.length} fires across {entries.length} workflow{entries.length !== 1 ? 's' : ''}
              </h3>
              {allUpcoming.length === 0 && !loading && (
                <div style={{ color: 'var(--text-tertiary)', fontSize: '14px', padding: '20px 0' }}>
                  No upcoming scheduled runs. Check that your workflows have a cron schedule and are not paused.
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {allUpcoming.map((ev, i) => {
                  const dayLabel = formatLocalDate(ev.time, { weekday: 'short', month: 'short', day: 'numeric' });
                  const prevLabel = i > 0 ? formatLocalDate(allUpcoming[i - 1].time, { weekday: 'short', month: 'short', day: 'numeric' }) : null;
                  return (
                    <React.Fragment key={`${ev.entry.id}-${i}`}>
                      {dayLabel !== prevLabel && (
                        <div style={{ marginTop: i > 0 ? 14 : 0, marginBottom: 5, fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {dayLabel}
                        </div>
                      )}
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '7px 12px',
                        background: 'var(--bg-secondary)', borderRadius: 6,
                        borderLeft: `3px solid ${ev.entry.is_paused ? '#9ca3af' : ev.color}`,
                      }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, minWidth: 50, color: 'var(--text-tertiary)' }}>{fmtTime(ev.time)}</span>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontWeight: 600, fontSize: '13px' }}>{ev.entry.name}</span>
                          <span style={{ marginLeft: 8, fontSize: '11px', color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>{ev.entry.cron_schedule}</span>
                        </div>
                        {ev.entry.is_paused && <span style={statusBadgeStyle('paused')}>paused</span>}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
              {entries.length > 0 && (
                <WorkflowControls entries={entries} loading={loading} togglingId={togglingId} onToggle={togglePause} onOpen={onOpenWorkflow} style={{ marginTop: 32 }} />
              )}
            </div>
          )}
        </div>

        {/* ── RUN DETAIL SIDE PANEL ── */}
        {selectedEvent && (
          <RunDetailPanel event={selectedEvent} onClose={() => setSelectedEvent(null)} onOpenWorkflow={onOpenWorkflow} />
        )}
      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────

function WorkflowControls({ entries, loading, togglingId, onToggle, onOpen, style }) {
  return (
    <div style={{ marginTop: 28, ...style }}>
      <h3 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 600, color: 'var(--text-secondary)' }}>Scheduled Workflows</h3>
      {entries.length === 0 && !loading && (
        <div style={{ color: 'var(--text-tertiary)', fontSize: '13px', padding: '16px 0' }}>
          No workflows with a cron schedule found.
        </div>
      )}
      {entries.map((entry, idx) => (
        <WorkflowRow
          key={entry.id}
          entry={entry}
          color={getWorkflowColor(entry.id, idx)}
          toggling={togglingId === entry.id}
          onToggle={() => onToggle(entry)}
          onOpen={onOpen ? () => onOpen(entry.id) : null}
        />
      ))}
    </div>
  );
}

function WorkflowRow({ entry, color, toggling, onToggle, onOpen }) {
  const lastRunTime = entry.last_run_time ? parseServerDateTime(entry.last_run_time) : null;
  const nextRunTime = entry.next_run_times?.[0] ? parseServerDateTime(entry.next_run_times[0]) : null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
      background: 'var(--bg-secondary)', borderRadius: 8, marginBottom: 6,
      borderLeft: `4px solid ${entry.is_paused ? '#9ca3af' : color}`,
      opacity: entry.is_paused ? 0.75 : 1,
    }}>
      <div style={{ width: 10, height: 10, borderRadius: '50%', background: entry.is_paused ? '#9ca3af' : color, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: '14px' }}>{entry.name}</span>
          {entry.is_paused && <span style={statusBadgeStyle('paused')}>paused</span>}
          {entry.last_run_status && !entry.is_paused && (
            <span style={statusBadgeStyle(entry.last_run_status)}>{entry.last_run_status}</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 14, marginTop: 3, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '12px', color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>{entry.cron_schedule}</span>
          {lastRunTime && (
            <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Last: {fmtDateTime(lastRunTime)}</span>
          )}
          {!entry.is_paused && nextRunTime && (
            <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Next: {fmtDateTime(nextRunTime)}</span>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        {onOpen && (
          <button onClick={onOpen} style={{ padding: '5px 10px', borderRadius: 5, border: '1px solid var(--border-color)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px' }}>
            Open
          </button>
        )}
        <button
          onClick={onToggle}
          disabled={toggling}
          style={{
            padding: '5px 12px', borderRadius: 5, border: 'none',
            background: entry.is_paused ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.12)',
            color: entry.is_paused ? '#22c55e' : '#ef4444',
            cursor: toggling ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 600,
            opacity: toggling ? 0.6 : 1,
          }}
        >
          {toggling ? '…' : entry.is_paused ? 'Resume' : 'Pause'}
        </button>
      </div>
    </div>
  );
}

function RunDetailPanel({ event, onClose, onOpenWorkflow }) {
  const { run, entry, color } = event;
  const sc = STATUS_COLORS[run.status] || '#9ca3af';
  const startTime = parseServerDateTime(run.start_time);
  const endTime = run.end_time ? parseServerDateTime(run.end_time) : null;

  const field = (label, value) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{value}</div>
    </div>
  );

  return (
    <div style={{ width: 320, flexShrink: 0, borderLeft: '1px solid var(--border-color)', background: 'var(--bg-secondary)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontWeight: 700, fontSize: '14px' }}>Run Details</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '20px', lineHeight: 1 }}>×</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '18px' }}>
        {/* Workflow */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, fontWeight: 600 }}>Workflow</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0, display: 'inline-block' }} />
            <span style={{ fontWeight: 600, fontSize: '14px' }}>{entry.name}</span>
          </div>
        </div>

        {/* Status */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, fontWeight: 600 }}>Status</div>
          <span style={{ display: 'inline-block', padding: '4px 10px', borderRadius: 12, background: `${sc}22`, color: sc, border: `1px solid ${sc}44`, fontWeight: 700, fontSize: '12px', textTransform: 'capitalize' }}>
            {run.status}
          </span>
        </div>

        {field('Start Time', startTime ? fmtDateTime(startTime) : '—')}
        {endTime && field('End Time', fmtDateTime(endTime))}
        {run.duration_s != null && field('Duration', run.duration_s < 60 ? `${run.duration_s.toFixed(1)}s` : `${(run.duration_s / 60).toFixed(1)}m`)}
        {run.trigger_type && field('Trigger', run.trigger_type)}
        {run.ai_cost_usd != null && run.ai_cost_usd > 0 && field('AI Cost', `$${run.ai_cost_usd.toFixed(4)}`)}

        {run.error && (
          <div style={{ marginBottom: 14, padding: '10px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 7 }}>
            <div style={{ fontSize: '10px', color: '#ef4444', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Error</div>
            <div style={{ fontSize: '11px', color: '#ef4444', fontFamily: 'monospace', wordBreak: 'break-all', maxHeight: 120, overflowY: 'auto' }}>{run.error}</div>
          </div>
        )}

        {onOpenWorkflow && (
          <button
            onClick={() => onOpenWorkflow(entry.id)}
            style={{ width: '100%', padding: '8px', borderRadius: 7, border: '1px solid var(--border-color)', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '13px', fontWeight: 500, marginTop: 4 }}
          >
            Open Workflow →
          </button>
        )}
      </div>
    </div>
  );
}
